import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ChipRow from '../Components/ChipRow'
import FollowButton from '../Components/FollowButton'
import { CloseIcon, SearchIcon } from '../Components/Icons'
import PageHeader from '../Components/PageHeader'
import { SourceAvatar } from '../Components/SourceAvatar'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { t } from '../i18n'
import { useFollow } from '../State/Follow'
import { useSources } from '../State/refData'
import { norm } from '../Utils/text'

const GROUPS = ['portal', 'tv_radio', 'local', 'sport', 'business']

/* "Izvori" (spec 5.9): every source, filter chips with counts (Svi, Pratiš,
   then by kind), follow from the list. A row opens the source. */
export default function Sources() {
  const navigate = useNavigate()
  const { sources } = useSources()
  const follow = useFollow()
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  useDocumentTitle(t('sources.title'))

  const followedCount = sources.filter((s) => follow.isSourceFollowed(s.slug)).length
  const counts = { all: sources.length, following: followedCount }
  GROUPS.forEach((g) => (counts[g] = sources.filter((s) => s.group === g).length))
  const filters = ['all', 'following', ...GROUPS.filter((g) => counts[g] > 0)]

  const key = norm(query)
  const list = sources.filter(
    (s) =>
      (filter === 'all' ||
        (filter === 'following' ? follow.isSourceFollowed(s.slug) : s.group === filter)) &&
      (!key || norm(s.name).includes(key))
  )
  const label = (f) =>
    f === 'all'
      ? t('sources.all')
      : f === 'following'
      ? t('sources.following')
      : t(`sources.groups.${f}`)

  return (
    <>
      <PageHeader title={t('sources.title')} />
      <div className="sources-top">
        <p className="muted">
          {t('sources.summary', { count: sources.length, followed: followedCount })}
        </p>
        <div className="search-field">
          <SearchIcon size={18} className="search-field-icon" />
          <input
            type="search"
            value={query}
            placeholder={t('sources.search')}
            aria-label={t('sources.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="icon-button search-clear"
              aria-label={t('common.clear')}
              onClick={() => setQuery('')}
            >
              <CloseIcon size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="sticky-bar sticky-under-header">
        <ChipRow label={t('sources.filters')} activeKey={filter}>
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              className={`chip ${filter === f ? 'chip-active' : ''}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              <span>{label(f)}</span>
              <span className="chip-count">{counts[f]}</span>
            </button>
          ))}
        </ChipRow>
      </div>

      {filter === 'following' && followedCount === 0 ? (
        <div className="feed-message">
          <p>{t('sources.emptyFollowing')}</p>
        </div>
      ) : list.length === 0 && key ? (
        <div className="feed-message">
          <p>{t('sources.noMatches', { query })}</p>
        </div>
      ) : (
        <ul className="source-list">
          {list.map((s) => (
            // The row opens the source; the button only follows.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <li key={s.slug} className="source-row" onClick={() => navigate(`/izvor/${s.slug}`)}>
              <SourceAvatar source={s} size={40} />
              <span className="source-row-text">
                <a
                  href={`/izvor/${s.slug}`}
                  className="source-row-name"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/izvor/${s.slug}`)
                  }}
                >
                  {s.name}
                </a>
                <span className="source-row-meta">{t(`sources.group.${s.group}`)}</span>
              </span>
              <FollowButton kind="source" item={s} variant="list" entry="sources_list" />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
